import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../Context/HimmaContext';
import {
  SalesReportRequest,
  CancelledOrdersReportRequest,
  OrderSummaryReportRequest,
  AdminCommissionReportRequest,
  RejectedOrdersReportRequest,
  PendingOrdersReportRequest,
  MerchantPayoutReportRequest,
  RefundReportRequest,
} from '../client-sdk/api';

export const useReportsApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadSalesReport = useCallback(
    async (request: SalesReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadSalesReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download sales report: No response data');
      } catch (err: unknown) {
        let message = 'An unexpected error occurred';

        // Try to extract backend message (handles blob error bodies too)
        const anyErr = err as { response?: { data?: unknown } };

        if (anyErr?.response?.data) {
          const data = anyErr.response.data;
          try {
            if (data instanceof Blob) {
              const text = await data.text();
              const parsed = JSON.parse(text);
              if (parsed?.message) {
                message = parsed.message;
              }
            } else if (typeof data === 'string') {
              const parsed = JSON.parse(data);
              if (parsed?.message) {
                message = parsed.message;
              }
            } else if (typeof data === 'object' && 'message' in data) {
              const obj = data as { message?: string };
              if (obj.message) {
                message = obj.message;
              }
            }
          } catch {
            // fall back to default message
          }
        } else if (err instanceof Error && err.message) {
          message = err.message;
        }

        setError(message);

        // Throw a shaped error so React Query's onError can show this message
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadOrderSummaryReport = useCallback(
    async (request: OrderSummaryReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadOrderSummaryReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download order summary report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadAdminCommissionReport = useCallback(
    async (request: AdminCommissionReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadAdminCommissionReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download admin commission report: No response data');
      } catch (err: unknown) {
        let message = 'An unexpected error occurred';

        // Try to extract backend message (handles blob error bodies too)
        const anyErr = err as { response?: { data?: unknown } };

        if (anyErr?.response?.data) {
          const data = anyErr.response.data;
          try {
            if (data instanceof Blob) {
              const text = await data.text();
              const parsed = JSON.parse(text);
              if (parsed?.message) {
                message = parsed.message;
              }
            } else if (typeof data === 'string') {
              const parsed = JSON.parse(data);
              if (parsed?.message) {
                message = parsed.message;
              }
            } else if (typeof data === 'object' && 'message' in data) {
              const obj = data as { message?: string };
              if (obj.message) {
                message = obj.message;
              }
            }
          } catch {
            // fall back to default message
          }
        } else if (err instanceof Error && err.message) {
          message = err.message;
        }

        setError(message);

        // Throw a shaped error so React Query's onError can show this message
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadCancelledOrdersReport = useCallback(
    async (request: CancelledOrdersReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadCancelledOrdersReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download cancelled orders report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadRejectedOrdersReport = useCallback(
    async (request: RejectedOrdersReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadRejectedOrdersReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download rejected orders report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadPendingOrdersReport = useCallback(
    async (request: PendingOrdersReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadPendingOrdersReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download pending orders report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadMerchantPayoutReport = useCallback(
    async (request: MerchantPayoutReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadMerchantPayoutReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download merchant payout report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadRefundReport = useCallback(
    async (request: RefundReportRequest): Promise<Blob> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadRefundReport(request, {
          responseType: 'blob',
        });
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to download refund report: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    downloadSalesReport,
    downloadOrderSummaryReport,
    downloadAdminCommissionReport,
    downloadCancelledOrdersReport,
    downloadRejectedOrdersReport,
    downloadPendingOrdersReport,
    downloadMerchantPayoutReport,
    downloadRefundReport,
    loading,
    error,
  };
};

